/**
 * TESSI Enhance vehicle + Commander handoff.
 * Shared state for configurator and PDPs.
 * Default: customer needs a Gen2 Commander.
 *
 * NOTE: 'knob' and 'dash' were intentionally removed from PRODUCT_PATHS below.
 * Both products now have a native, server-rendered Commander-first question built into
 * snippets/s3xy-vehicle-select-v2.liquid (the in-modal "Besitzt du bereits einen Commander?"
 * step). Keeping them here as well caused a SECOND, conflicting Commander widget to be
 * injected on those two product pages, and interceptVehicleTileClicks() below would rewrite
 * the already-correct variant chosen by the new in-modal step using this script's own,
 * separate localStorage-based commander status - i.e. two independent sources of truth for
 * the same decision. 'buttons' and 'stalks' still rely entirely on this script (they don't
 * use the v2 widget yet), so they remain untouched.
 */
(() => {
  const STORAGE_KEY = 'tessiEnhanceConfigV1';
  const COMMANDER_PARAM = 's3xy_commander';
  const VEHICLE_VARIANT_PARAM = 's3xy_vehicle_variant';

  const PRODUCT_PATHS = {
    buttons: '/products/s3xy-buttons-v2-passend-fur-deinen-tesla-model-3-y',
    stalks: '/products/s3xy-stalks-fur-tesla-model-3-highland'
  };

  const getState = () => {
    try { return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}'); }
    catch (error) { return {}; }
  };

  const saveState = (patch) => {
    const next = { ...getState(), ...patch };
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); }
    catch (error) {}
    return next;
  };

  const getModelKey = () => {
    const state = getState();
    const model = typeof state.model === 'string' ? state.model : '';
    if (state.family === 'm3' || model.startsWith('m3-')) return 'model3';
    if (state.family === 'my' || model.startsWith('my-')) return 'modely';
    return '';
  };

  const getCommanderStatus = () => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get(COMMANDER_PARAM);
    if (fromUrl === 'yes' || fromUrl === 'no') return fromUrl;
    const saved = getState().commander;
    if (saved === 'yes' || saved === 'no') return saved;
    return 'no';
  };

  const ensureCommanderDefault = () => {
    const state = getState();
    if (state.commander !== 'yes' && state.commander !== 'no') saveState({ commander: 'no' });

    const kind = getProductKindFromPath();
    if (!kind) return false;

    const url = new URL(window.location.href);
    let changed = false;
    if (!url.searchParams.get(COMMANDER_PARAM)) {
      url.searchParams.set(COMMANDER_PARAM, 'no');
      changed = true;
    }

    if (kind === 'buttons' || kind === 'stalks') {
      const resolved = resolveCommanderVariant(kind, 'no', '');
      if (resolved && url.searchParams.get('variant') !== resolved) {
        url.searchParams.set('variant', resolved);
        changed = true;
      }
    }

    if (changed) {
      window.location.replace(`${url.pathname}${url.search}${url.hash}`);
      return true;
    }
    return false;
  };

  const parseVariantMap = (value) => {
    const map = {};
    String(value || '').split(',').forEach((pair) => {
      const index = pair.indexOf(':');
      if (index === -1) return;
      const key = pair.slice(0, index).trim();
      const variantId = pair.slice(index + 1).trim();
      if (key && variantId) map[key] = variantId;
    });
    return map;
  };

  const getProductKindFromTitle = (title) => {
    const value = String(title || '').toLowerCase();
    if (value.includes('knob')) return 'knob';
    if (value.includes('dash')) return 'dash';
    if (value.includes('button')) return 'buttons';
    if (value.includes('stalk')) return 'stalks';
    return '';
  };

  const getProductKindFromPath = () => {
    const path = window.location.pathname.replace(/\/$/, '');
    return Object.keys(PRODUCT_PATHS).find((key) => path === PRODUCT_PATHS[key]) || '';
  };

  const resolveCommanderVariant = (kind, status, baseVehicleVariant) => {
    const base = String(baseVehicleVariant || '');
    if (status !== 'yes' && status !== 'no') return base;

    if (kind === 'dash') return status === 'yes' ? '52794788774152' : base;

    if (kind === 'knob') {
      const withExistingCommander = {
        '49988240802056': '49988240802056',
        '49988240769288': '49988240769288',
        '51059962839304': '49988240769288',
        '57747710443784': '49988240769288'
      };
      const needsCommander = {
        '49988240802056': '45409814511880',
        '49988240769288': '45409814577416',
        '51059962839304': '51059962839304',
        '57747710443784': '57747710443784'
      };
      return (status === 'yes' ? withExistingCommander : needsCommander)[base] || '';
    }

    if (kind === 'buttons') return status === 'yes' ? '45409532707080' : '45409532641544';
    if (kind === 'stalks') return status === 'yes' ? '50404081434888' : '50404081467656';
    return base;
  };

  const patchDashMappings = () => {
    document.querySelectorAll('[id^="TessiEnhance-"] .teh-card[data-product-title="S3XY Dash"]').forEach((card) => {
      const map = parseVariantMap(card.dataset.variantMap);
      map['m3-highland-standard-2026'] = '52779500339464';
      map['my-juniper-standard-early'] = '52779501256968';
      card.dataset.variantMap = Object.entries(map).map(([key, value]) => `${key}:${value}`).join(',');
    });
  };

  const refreshConfiguratorLinks = () => {
    patchDashMappings();
    const state = getState();
    const modelKey = getModelKey();
    const commander = state.commander === 'yes' || state.commander === 'no' ? state.commander : 'no';

    document.querySelectorAll('[id^="TessiEnhance-"] .teh-card').forEach((card) => {
      const link = card.querySelector('[data-product-link][data-base-url]');
      if (!link) return;

      const kind = getProductKindFromTitle(card.dataset.productTitle);
      const variantMap = parseVariantMap(card.dataset.variantMap);
      const baseVehicleVariant = state.model ? String(variantMap[state.model] || '') : '';
      const resolvedVariant = kind ? resolveCommanderVariant(kind, commander, baseVehicleVariant) : baseVehicleVariant;

      let url;
      try { url = new URL(link.dataset.baseUrl, window.location.origin); }
      catch (error) { return; }

      if (resolvedVariant) url.searchParams.set('variant', resolvedVariant);
      if (modelKey) url.searchParams.set('s3xy_model', modelKey);
      if (baseVehicleVariant && (kind === 'knob' || kind === 'dash')) url.searchParams.set(VEHICLE_VARIANT_PARAM, baseVehicleVariant);
      url.searchParams.set(COMMANDER_PARAM, commander);
      link.href = `${url.pathname}${url.search}${url.hash}`;
    });
  };

  const repairProductPageUrl = () => {
    const kind = getProductKindFromPath();
    if (!kind) return;
    const modelKey = getModelKey();
    if (!modelKey) return;

    const url = new URL(window.location.href);
    if (url.searchParams.get('s3xy_model')) return;
    const vehicleVariant = url.searchParams.get(VEHICLE_VARIANT_PARAM) || url.searchParams.get('variant');
    if (!vehicleVariant) return;

    url.searchParams.set('s3xy_model', modelKey);
    if ((kind === 'knob' || kind === 'dash') && !url.searchParams.get(VEHICLE_VARIANT_PARAM)) url.searchParams.set(VEHICLE_VARIANT_PARAM, vehicleVariant);
    window.location.replace(`${url.pathname}${url.search}${url.hash}`);
  };

  const buildCommanderSelector = () => {
    const kind = getProductKindFromPath();
    if (!kind || document.getElementById('s3xy-commander-select')) return;

    const wrap = document.createElement('div');
    wrap.id = 's3xy-commander-select';
    wrap.className = 's3xy-commander';
    wrap.innerHTML = `
      <p class="s3xy-commander__title">Benötigst du einen Gen2 Commander?</p>
      <div class="s3xy-commander__choices">
        <button type="button" class="s3xy-commander__choice" data-s3xy-commander="no">Ja, ich benötige einen Gen2 Commander</button>
        <button type="button" class="s3xy-commander__choice" data-s3xy-commander="yes">Nein, ich habe bereits einen Gen2 Commander</button>
      </div>
      <p class="s3xy-commander__hint"><strong>Für ca. 98 % unserer Nutzer ist „Ja" die richtige Auswahl</strong>, da sie bisher noch keinen Gen2 Commander im Einsatz haben. Wir wählen automatisch die passende Produktvariante.</p>
    `;

    const vehicleSelector = document.querySelector('.s3xy-vs');
    const atc = document.querySelector('[data-product-add-to-cart-button]');
    const productForm = atc ? atc.closest('form') : document.querySelector('form[action*="/cart/add"]');
    if (vehicleSelector) vehicleSelector.insertAdjacentElement('afterend', wrap);
    else if (productForm) productForm.insertAdjacentElement('beforebegin', wrap);
    else return;

    if (!document.getElementById('s3xy-commander-style')) {
      const style = document.createElement('style');
      style.id = 's3xy-commander-style';
      style.textContent = `
        .s3xy-commander{margin:0 0 16px;padding:16px;border:1px solid rgba(0,0,0,.12);border-radius:8px;background:rgba(0,0,0,.025)}
        .s3xy-commander__title{margin:0 0 10px;font-weight:600;font-size:1.02em}
        .s3xy-commander__choices{display:grid;grid-template-columns:1fr 1fr;gap:8px}
        .s3xy-commander__choice{padding:12px 10px;border:1px solid rgba(0,0,0,.18);border-radius:6px;background:transparent;color:inherit;cursor:pointer;font:inherit}
        .s3xy-commander__choice.is-active{border-color:currentColor;background:rgba(0,0,0,.07);font-weight:600}
        .s3xy-commander__hint{margin:9px 0 0;font-size:.85em;opacity:.78;line-height:1.4}
        @media(max-width:600px){.s3xy-commander__choices{grid-template-columns:1fr}}
      `;
      document.head.appendChild(style);
    }

    const setActive = (status) => {
      wrap.querySelectorAll('[data-s3xy-commander]').forEach((button) => button.classList.toggle('is-active', button.dataset.s3xyCommander === status));
    };
    setActive(getCommanderStatus());

    wrap.addEventListener('click', (event) => {
      const button = event.target.closest('[data-s3xy-commander]');
      if (!button) return;
      const status = button.dataset.s3xyCommander;
      saveState({ commander: status });
      setActive(status);

      const url = new URL(window.location.href);
      const baseVehicleVariant = url.searchParams.get(VEHICLE_VARIANT_PARAM) || ((kind === 'knob' || kind === 'dash') && url.searchParams.get('s3xy_model') ? url.searchParams.get('variant') : '');
      const resolvedVariant = resolveCommanderVariant(kind, status, baseVehicleVariant);

      url.searchParams.set(COMMANDER_PARAM, status);
      if (baseVehicleVariant && (kind === 'knob' || kind === 'dash')) url.searchParams.set(VEHICLE_VARIANT_PARAM, baseVehicleVariant);
      if (resolvedVariant) url.searchParams.set('variant', resolvedVariant);
      window.location.assign(`${url.pathname}${url.search}${url.hash}`);
    });
  };

  const interceptVehicleTileClicks = () => {
    const kind = getProductKindFromPath();
    if (kind !== 'knob' && kind !== 'dash') return;

    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const tile = target ? target.closest('.s3xy-vs__variant-tile[href]') : null;
      if (!tile) return;

      const commander = getCommanderStatus();
      let url;
      try { url = new URL(tile.href, window.location.origin); }
      catch (error) { return; }

      const baseVehicleVariant = url.searchParams.get('variant');
      const resolvedVariant = resolveCommanderVariant(kind, commander, baseVehicleVariant);
      if (!baseVehicleVariant || !resolvedVariant) return;

      url.searchParams.set('variant', resolvedVariant);
      url.searchParams.set(VEHICLE_VARIANT_PARAM, baseVehicleVariant);
      url.searchParams.set(COMMANDER_PARAM, commander);
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.assign(`${url.pathname}${url.search}${url.hash}`);
    }, true);
  };

  const run = () => {
    if (ensureCommanderDefault()) return;
    refreshConfiguratorLinks();
    repairProductPageUrl();
    buildCommanderSelector();
    interceptVehicleTileClicks();
  };

  document.addEventListener('DOMContentLoaded', run);
  if (document.readyState !== 'loading') run();

  ['vehicle_selected', 'enhance_goal_selected', 'commander_status_selected'].forEach((eventName) => document.addEventListener(eventName, refreshConfiguratorLinks));

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target ? target.closest('[id^="TessiEnhance-"] [data-product-link]') : null;
    if (!link) return;
    refreshConfiguratorLinks();
    const currentLink = target.closest('[id^="TessiEnhance-"] [data-product-link]');
    if (!currentLink) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.assign(currentLink.href);
  }, true);
})();

/**
 * Kompletträder-PDP: Design-Award-Chips + Produktinformationen-Auto-Open.
 * Auf Razor- UND Erion-Produkten (H1-Match) wird das "Produktinformationen"-Akkordeon
 * standardmäßig geöffnet, damit die gestaltete Produktbeschreibung sofort sichtbar ist.
 * Die zwei Award-Badges (Red Dot 2020, iF Design Award) werden weiterhin NUR auf
 * The-New-Aero-Razor-Produkten in die Hero-Badge-Zeile (.kr__badges) eingefügt —
 * Erion hat keine Design-Awards. Styling: .kr__badge--award in assets/custom.css.
 */
(() => {
  const run = () => {
    const h1 = document.querySelector('.kr__h1');
    const title = h1 ? (h1.textContent || '') : '';
    if (!/razor|erion/i.test(title)) return;

    if (/razor/i.test(title)) {
      const badges = document.querySelector('.kr__badges');
      if (badges && !badges.querySelector('.kr__badge--award')) {
        ['Red Dot Award 2020 · Product Design', 'iF Design Award'].forEach((label) => {
          const chip = document.createElement('span');
          chip.className = 'kr__badge kr__badge--award';
          chip.textContent = label;
          badges.appendChild(chip);
        });
      }
    }

    const toggle = Array.prototype.find.call(
      document.querySelectorAll('button.kr__specs-toggle'),
      (btn) => (btn.textContent || '').trim().indexOf('Produktinformationen') === 0
    );
    if (toggle && toggle.getAttribute('aria-expanded') !== 'true') toggle.click();
  };
  document.addEventListener('DOMContentLoaded', run);
  if (document.readyState !== 'loading') run();
})();

/**
 * Kompletträder-PDP: Sprung-Chip-Navigation (Schnellnavigation).
 * Desktop: sticky Chip-Leiste nach dem Hero (.kr__hero), Chips springen zu den
 * Theme-Sektionen (Reifenwähler, Produktinformationen, Technische Details,
 * Lieferung & Wechsel, Bewertungen). Ein Chip erscheint nur, wenn sein Sprungziel auf
 * der Seite existiert — "Made in Germany" z. B. nur, wenn die Produktbeschreibung das
 * Anker-Element #tna-made-in-germany enthält (aktuell TNA Razor).
 * Mobil (<900px): zusätzlich fixe Bottom-Bar #kr-chipnav-bottom mit den drei wichtigsten
 * Aktionen (Produktinfos, Reifen & Preise, WhatsApp); sie positioniert sich dynamisch
 * oberhalb des Theme-Sticky-Buttons (.kr__sticky-button), falls dieser sichtbar ist.
 * Ziele in eingeklappten Akkordeons werden vor dem Scrollen geöffnet. Der zweite,
 * instante scrollIntoView nach 900 ms korrigiert Lazy-Load-Verschiebungen.
 * Styling: #kr-chipnav / #kr-chipnav-bottom / .kr-chipnav__chip in assets/custom.css.
 */
(() => {
  const run = () => {
    const hero = document.querySelector('.kr__hero');
    if (!hero || document.getElementById('kr-chipnav')) return;

    const findHeading = (prefixes) => Array.prototype.find.call(
      document.querySelectorAll('h2, h3'),
      (h) => {
        const t = (h.textContent || '').trim();
        return prefixes.some((p) => t.indexOf(p) === 0);
      }
    );

    const openCollapsedFor = (el) => {
      const coll = el.closest ? el.closest('.collapsible') : null;
      let btn = null;
      if (coll) {
        let p = coll.previousElementSibling;
        while (p && !(p.classList && p.classList.contains('kr__specs-toggle'))) p = p.previousElementSibling;
        btn = p;
      } else if (el.classList && el.classList.contains('kr__specs')) {
        btn = el.querySelector('.kr__specs-toggle');
      }
      if (btn && btn.getAttribute('aria-expanded') !== 'true') { btn.click(); return 400; }
      return 0;
    };

    const goTo = (el) => {
      const delay = openCollapsedFor(el);
      el.style.scrollMarginTop = '70px';
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(() => { el.scrollIntoView({ block: 'start' }); }, 900);
      }, delay);
    };

    const specsGrp = document.querySelector('.kr__specs');
    const reifenHeading = findHeading(['Wähle deine Reifen']);
    // EU-Label-Reifentabelle aus der Produktbeschreibung (Spalte "Nasshaftung"), siehe snippets/kr-tire-recommendation.liquid
    const tireTable = Array.prototype.find.call(document.querySelectorAll('table'), (t) => /Nasshaftung/.test(t.textContent || ''));

    const targets = [
      { label: 'Reifen & Preise', el: reifenHeading },
      { label: 'Reifen-Bewertung', el: tireTable },
      { label: 'Passform & Gutachten', el: specsGrp },
      { label: 'Technische Daten', el: findHeading(['Technische Details', 'Technische Daten']) },
      { label: 'Lieferung & Montage', el: findHeading(['Lieferung & Wechsel', 'Lieferung und Wechsel', 'Versandbedingungen']) },
      { label: 'Bewertungen', el: findHeading(['Das sagen Tesla-Fahrer']) },
      { label: 'Made in Germany', el: document.getElementById('tna-made-in-germany') }
    ].filter((t) => t.el);
    if (targets.length < 2) return;

    const bar = document.createElement('nav');
    bar.id = 'kr-chipnav';
    bar.setAttribute('aria-label', 'Schnellnavigation');
    const row = document.createElement('div');
    row.className = 'kr-chipnav__row';

    targets.forEach((t) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'kr-chipnav__chip';
      chip.textContent = t.label;
      chip.addEventListener('click', () => goTo(t.el));
      row.appendChild(chip);
    });

    const makeWhatsApp = (label) => {
      const wa = document.createElement('a');
      wa.className = 'kr-chipnav__chip kr-chipnav__chip--action';
      wa.href = 'https://wa.me/4915233538807';
      wa.target = '_blank';
      wa.rel = 'noopener';
      wa.textContent = label;
      return wa;
    };

    row.appendChild(makeWhatsApp('WhatsApp-Beratung'));
    bar.appendChild(row);
    hero.insertAdjacentElement('afterend', bar);

    // Mobile Bottom-Bar mit den drei wichtigsten Aktionen
    if (window.matchMedia('(max-width: 899px)').matches && (specsGrp || reifenHeading)) {
      const bbar = document.createElement('nav');
      bbar.id = 'kr-chipnav-bottom';
      bbar.setAttribute('aria-label', 'Schnellzugriff');
      const mkBtn = (label, el) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'kr-chipnav__chip';
        b.textContent = label;
        b.addEventListener('click', () => goTo(el));
        return b;
      };
      if (specsGrp) bbar.appendChild(mkBtn('Produktinfos', specsGrp));
      if (reifenHeading) bbar.appendChild(mkBtn('Reifen & Preise', reifenHeading));
      bbar.appendChild(makeWhatsApp('WhatsApp'));
      document.body.appendChild(bbar);

      // Oberhalb des Theme-Sticky-Buttons positionieren, falls dieser gerade sichtbar ist
      const stickyBtn = document.querySelector('.kr__sticky-button');
      const place = () => {
        let offset = 0;
        if (stickyBtn) {
          const r = stickyBtn.getBoundingClientRect();
          if (r.height > 0 && r.top < window.innerHeight && r.bottom > 0) {
            offset = Math.max(0, window.innerHeight - r.top);
          }
        }
        bbar.style.bottom = offset + 'px';
      };
      place();
      window.addEventListener('resize', place, { passive: true });
      window.addEventListener('scroll', place, { passive: true });
    }
  };
  document.addEventListener('DOMContentLoaded', run);
  if (document.readyState !== 'loading') run();
})();

/**
 * Erion-10-Vorbestell-Popup (Kompletträder-PDP).
 * Erscheint NUR auf den Erion-Matt-Varianten (H1 enthält „Erion" UND „Schwarz Matt" oder
 * „Grau Matt" — das sind die Q1-2027-Vorbestellungen), deren Reifenauswahl Vorbestell-
 * Optionen („Lieferung ab …") und eine „Unmontiert"-Auswahl bietet. Auf den
 * Chrome-Shadow-Seiten (KW44) erscheint es bewusst NICHT; fällt die Vorbestellung später
 * weg, verschwindet es auch auf den Matt-Seiten automatisch. Empfiehlt das unmontierte Set:
 * Reifen/Sensoren werden vorab geliefert (Montage auf die originalen Stahlfelgen), die
 * Sommerreifen kommen später auf die Erion 10.
 * CTA „Unmontiertes Set auswählen": aktiviert den Saison-Tab „Unmontierte Räder"
 * (.kr__season-tab) und scrollt zur Überschrift „Wähle deine Reifen" — der Kunde sieht
 * dort die Kategorien (Winter-/Allwetterkomplettrad/Unmontiert) mit „Unmontiert" vorgewählt
 * und trifft die Reifenwahl selbst (Reifen-Gate bleibt bis dahin regulär gesperrt).
 * Scroll wie bei der Chip-Navigation: 350 ms nach Tab-Klick (Re-Render abwarten) smooth,
 * nach weiteren 900 ms instanter Korrektur-Scroll gegen Lazy-Load-Verschiebungen.
 * Fallback ohne Tabs: erste unmontierte Option direkt anwählen. Erscheint einmal pro
 * Browser-Session (sessionStorage), schließbar per X, Overlay-Klick und Escape.
 * Styling: .kr-preorder-popup* in assets/custom.css.
 */
(() => {
  const KEY = 'krErionPreorderPopupV1';
  const run = () => {
    const h1 = document.querySelector('.kr__h1');
    const h1Text = h1 ? (h1.textContent || '') : '';
    if (!/erion/i.test(h1Text) || !/(schwarz|grau)\s*matt/i.test(h1Text)) return;
    if (document.getElementById('kr-preorder-popup')) return;
    try { if (window.sessionStorage.getItem(KEY)) return; } catch (e) {}

    const inputs = Array.prototype.slice.call(document.querySelectorAll('.kr__wheel-input'));
    if (!inputs.length) return;
    const optionBox = (input) => input.closest('label') || input.closest('.kr__wheel') || input.parentElement;
    const texts = inputs.map((i) => { const b = optionBox(i); return b ? (b.textContent || '') : ''; });
    const isPreorder = texts.some((t) => /lieferung ab/i.test(t));
    const unmountedIndex = texts.findIndex((t) => /unmontiert/i.test(t));
    const findUnmountedTab = () => Array.prototype.find.call(
      document.querySelectorAll('.kr__season-tab'),
      (t) => /unmontiert/i.test(t.textContent || '')
    );
    if (!isPreorder || (!findUnmountedTab() && unmountedIndex === -1)) return;

    const onKey = (ev) => { if (ev.key === 'Escape') dismiss(); };
    const dismiss = () => {
      try { window.sessionStorage.setItem(KEY, '1'); } catch (e) {}
      const el = document.getElementById('kr-preorder-popup');
      if (el) el.remove();
      document.removeEventListener('keydown', onKey);
    };

    const overlay = document.createElement('div');
    overlay.id = 'kr-preorder-popup';
    overlay.className = 'kr-preorder-popup';
    overlay.innerHTML = [
      '<div class="kr-preorder-popup__dialog" role="dialog" aria-modal="true" aria-labelledby="kr-preorder-popup-title">',
      '<button type="button" class="kr-preorder-popup__close" aria-label="Schließen">&times;</button>',
      '<p id="kr-preorder-popup-title" class="kr-preorder-popup__title">Unsere Empfehlung für die beste Optik</p>',
      '<p class="kr-preorder-popup__text">Wähle bei den vorbestellbaren Erion-10-Varianten am besten das unmontierte Set.</p>',
      '<p class="kr-preorder-popup__text">Die Winterreifen und Sensoren senden wir dir bereits vorab zu. So kannst du die Winterreifen direkt auf deine originalen Tesla-Stahlfelgen montieren lassen.</p>',
      '<p class="kr-preorder-popup__text">Sobald deine Erion-10-Designstahlfelgen verfügbar sind, lässt du einfach die originalen Sommerreifen auf die Erion 10 montieren. So fährst du im Sommer das attraktivere Erion-10-Design und nutzt die originalen Stahlfelgen sinnvoll für den Winter.</p>',
      '<button type="button" class="kr-preorder-popup__cta">Unmontiertes Set auswählen</button>',
      '<button type="button" class="kr-preorder-popup__skip">Weiter ohne Empfehlung</button>',
      '</div>'
    ].join('');

    overlay.addEventListener('click', (ev) => { if (ev.target === overlay) dismiss(); });
    overlay.querySelector('.kr-preorder-popup__close').addEventListener('click', dismiss);
    overlay.querySelector('.kr-preorder-popup__skip').addEventListener('click', dismiss);
    overlay.querySelector('.kr-preorder-popup__cta').addEventListener('click', () => {
      dismiss();
      const tab = findUnmountedTab();
      let target = null;
      if (tab) {
        if (tab.getAttribute('aria-selected') !== 'true') tab.click();
        target = tab;
      } else if (unmountedIndex !== -1) {
        const input = inputs[unmountedIndex];
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        target = input ? optionBox(input) : null;
      }
      setTimeout(() => {
        const heading = Array.prototype.find.call(
          document.querySelectorAll('h2, h3'),
          (h) => (h.textContent || '').trim().indexOf('Wähle deine Reifen') === 0
        );
        const scrollEl = heading || target;
        if (!scrollEl) return;
        scrollEl.style.scrollMarginTop = '80px';
        scrollEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(() => { scrollEl.scrollIntoView({ block: 'start' }); }, 900);
      }, 350);
    });
    document.addEventListener('keydown', onKey);
    document.body.appendChild(overlay);
  };
  const start = () => { setTimeout(run, 1200); };
  document.addEventListener('DOMContentLoaded', start);
  if (document.readyState !== 'loading') start();
})();
